import React, { useEffect, useState } from 'react';
import { Grid, TextField } from '@material-ui/core';

import { useTranslation } from 'react-i18next';

import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { sawmillActions } from 'actions/sawmillActions';
import { AxiosError } from 'axios';
import { notificationActions } from 'actions/notificationActions';
import { CreateEditDialog } from './CreateEditDialog';
import { SawmillResponse } from '.generated/api';

interface ParamTypes {
  orgId: string;
}

type CreateEditSawmillDialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  sawmill?: SawmillResponse;
};

const CreateEditSawmillDialog: React.FC<CreateEditSawmillDialogProps> = ({
  isOpen,
  handleClose,
  sawmill,
}) => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();
  const { orgId } = useParams<ParamTypes>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInputValid, setInputValid] = useState<boolean>(true);
  const [newSawmill, setNewSawmill] = useState<{ name: string }>({
    name: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, name } = e.target;
    setNewSawmill((newSawmill) => ({ ...newSawmill, [name]: value }));
  };

  const createEditCallback = (error?: AxiosError) => {
    setIsLoading(false);
    if (error) {
      dispatch(notificationActions.enqueueErrorResponse(error));
    } else {
      handleCloseDialog();
    }
  };

  const handleCloseDialog = () => {
    setNewSawmill({
      name: '',
    });
    handleClose();
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (sawmill) {
      dispatch(
        sawmillActions.renameSawmill(
          sawmill.id,
          newSawmill.name,
          createEditCallback,
        ),
      );
    } else {
      dispatch(
        sawmillActions.addSawmillToOrganization(
          orgId,
          newSawmill.name,
          createEditCallback,
        ),
      );
    }
  };

  // validation
  useEffect(() => {
    setInputValid(newSawmill.name.length > 0);
  }, [newSawmill]);

  useEffect(() => {
    if (sawmill) {
      setNewSawmill({
        name: sawmill.name,
      });
    } else {
      setNewSawmill({
        name: '',
      });
    }
  }, [sawmill]);

  return (
    <CreateEditDialog
      title={
        sawmill
          ? t('organization_page.sawmills.dialog.edit_sawmill')
          : t('organization_page.sawmills.dialog.create_sawmill')
      }
      isOpen={isOpen}
      isLoading={isLoading}
      editMode={!!sawmill}
      canSubmit={isInputValid}
      onSubmit={handleSubmit}
      onClose={handleCloseDialog}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            autoFocus
            type="text"
            variant="outlined"
            margin="none"
            fullWidth
            label={t('organization_page.sawmills.labels.sawmill_name')}
            name="name"
            value={newSawmill.name}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
    </CreateEditDialog>
  );
};

export { CreateEditSawmillDialog };
